import { Pill, ScreenReaderContent, SimpleSelect } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { formatToReadableTime } from "../../utils/utils";

/**
 * MeasureVersionsSelect — Inline selector displaying all versions (draft + published)
 * for a qualitative measure response.
 *
 * Renders a SimpleSelect with options labeled as "{author}: {date}".
 * Selecting an option triggers a callback so the parent can populate the RTE.
 *
 * @param {Object[]}  versions          - All versions (draft + published), ordered newest first.
 *                                        Each entry: { id, author, last_modified, content, is_draft, is_latest }
 * @param {string}    selectedVersionId - The id of the currently selected version.
 * @param {Function}  onVersionSelect   - Callback invoked with the selected version's id.
 */
export const MeasureVersionsSelect = ({
  versions = [],
  selectedVersionId,
  onVersionSelect,
}) => {
  const { t } = useTranslation();

  const getOptionLabel = (version) => {
    const author = version.author || t("assessment.unknownUser");
    const date = formatToReadableTime(new Date(version.last_modified));
    return `${author}: ${date}`;
  };

  const getAfterInputIcon = () => {
    if (versions.find((v) => v.id === selectedVersionId)?.is_draft) {
      return <Pill color="success">{t("global.draft")}</Pill>;
    } else if (versions.find((v) => v.id === selectedVersionId)?.is_latest) {
      return <Pill color="success">{t("reflections.current")}</Pill>;
    } else {
      return undefined;
    }
  };

  const getAfterLabelIcon = (version) => {
    if (version.is_draft) {
      return <Pill color="success">{t("global.draft")}</Pill>;
    } else if (version.is_latest) {
      return <Pill color="success">{t("reflections.current")}</Pill>;
    } else {
      return undefined;
    }
  };

  return (
    <SimpleSelect
      renderLabel={
        <ScreenReaderContent>
          {t("results.measureVersionHistory")}
        </ScreenReaderContent>
      }
      value={selectedVersionId}
      onChange={(_e, { value }) => onVersionSelect(value)}
      width="auto"
      size="small"
      renderAfterInput={getAfterInputIcon()}
    >
      {versions.map((version) => (
        <SimpleSelect.Option
          id={`version-${version.id}`}
          key={version.id}
          value={version.id}
          renderAfterLabel={getAfterLabelIcon(version)}
        >
          {getOptionLabel(version)}
        </SimpleSelect.Option>
      ))}
    </SimpleSelect>
  );
};
